<script setup>
import { getBusinessChannel } from '@/api/business'
import { getInventoryByProductId, getStockAllocations, saveStockAllocations, getStockAllocationHistory } from '@/api/inventory'
import { getProductDetail } from '@/api/product'
import { getHashWarehouseList } from '@/api/warehouse'
import IconStockAllocated from '@/components/Icons/Inventory/IconStockAllocated.vue'
import IconStockOnHand from '@/components/Icons/Inventory/IconStockOnHand.vue'
import IconStockSellable from '@/components/Icons/Inventory/IconStockSellable.vue'
import { salesChannelIcon } from '@/utils/salesChannelIcon'
import {
  templateRef,
  useAsyncState,
  useResizeObserver,
} from '@vueuse/core'
import { computed, getCurrentInstance, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { i18n } from '@/i18n'
import moment from 'moment'


const { $notification } = getCurrentInstance().proxy.$root

const route = useRoute()
const router = useRouter()

const id = computed(() => route.params.product_id || route.params.id)

const { state: product, isLoading: isProductFetching } = useAsyncState(
  async () =>
    await getProductDetail({ id: id.value }).then(({ data }) => data?.data),
  null,
)

/**
 * 
 * @param {import('@/api/inventory.d').StockAllocationResponse['data']} allocationItems 
 * @param warehouses 
 * @param channels 
 * @param inventories 
 */
function parseAllocationItem(
  allocationItems,
  warehouses,
  channels,
  inventories,
) {
  /**
   * @type {import('@/types/allocation').CatalogAllocationDetailByWarehouse}
   */
  const items = []

  for (const inventory of inventories.warehouses) {
    const warehouseId = inventory.id
    if (!items?.[warehouseId]) {
      items[warehouseId] = {
        name: warehouses[warehouseId]?.name || i18n.t('productAvailability.unknown_warehouse'),
        id: warehouseId,
        unknown: false,
        counts: 0,
        items: [],
      }
    }

    const allocationCatalogs = (allocationItems.find(item => item.warehouse_id === warehouseId)?.catalogs) || [];


    (product.value.catalogs || []).forEach((catalog, index) => {
      const inventoryCatalog = inventory.catalogs.find(c => c.id === catalog.id)

      if (!inventoryCatalog) {
        return
      }

      const allocationCatalog = allocationCatalogs.find(c => c.catalog_id === inventoryCatalog.id)
      const allocations = (allocationCatalog?.allocations || [])
          .map(item => ({
            ...item,
            info: channels.find(channel => channel.id === item.channel_id),
          }))
          .toSorted((a, b) => a.channel_id - b.channel_id)

      items[warehouseId].counts += allocations.length
      items[warehouseId].items.push({
        catalog_id: catalog.id,
        title: catalog.title,
        sku: catalog.sku,
        inventory: inventoryCatalog,
        enable: allocationCatalog?.enable ?? true,
        allocations: allocations,
      })
    })
  }

  const results = Object.values(items).toSorted((a, b) => {
    return ((a.unknown ? '9999-' : '')  +!a.counts + a.name.toLowerCase()).localeCompare(
      (b.unknown ? '9999-' : '') + !b.counts + b.name.toLowerCase(),
    )
  })
  return results
}

const businessChannels = ref([])

const { state: allocations, execute: fetchAllocations, isLoading } = useAsyncState(
  async () => {
    const [allocationItems, warehouses, channels, inventories] =
      await Promise.all([
        getStockAllocations(product.value.product_id, {
          headers: {
            'Business-Id': product.value.business_id,
          },
        }).then(({ data }) => data?.data || []),
        getHashWarehouseList(product.value.business_id),
        getBusinessChannel(product.value.business_id).then(
          ({ data }) => {
            const list = data?.data?.channels || []
            businessChannels.value = list
            return list
          },
        ),
        getInventoryByProductId({
          product_id: product.value.product_id,
          params: { business_id: product.value.business_id },
        }).then(({ data }) => data?.data),
      ])

    return parseAllocationItem(
      allocationItems,
      warehouses,
      channels,
      inventories,
    )
  },
  {},
  {
    immediate: false,
    shallow: false,
    onError: err => {
      console.error(err);
      $notification.error({
        message: i18n.t('common.error_occurred'),
        description: err.message,
      })
    },
  },
)

/**
 * 
 * @typedef {import('@/types/allocation').CatalogAllocationDetail} CatalogAllocationDetail
 * 
 * @param {CatalogAllocationDetail['channels'][0]} allocationChannel 
 * @param {CatalogAllocationDetail['inventory']} inventory 
 */
function calculateAllocationAmount(allocationChannel, inventory) {
  return Math.floor(allocationChannel.allocate_value / 100 * inventory.sellable)
}

const tabPosition = ref('left')
const wrapper = templateRef('wrapper')
useResizeObserver(wrapper, entries => {
  const entry = entries[0]
  const width = entry.contentRect.width
  tabPosition.value = width < 768 ? 'top' : 'left'
})

watch(
  () => product.value?.product_id,
  (id, old) => {
    if (id && id !== old) fetchAllocations()
  },
)

const selectedWarehouse = computed(
  () => route.query.warehouse_id || allocations.value?.[0]?.id,
)

const saving = ref(false)
async function saveAllocation() {
  saving.value = true
  try {
    await saveStockAllocations(
      product.value.business_id,
      product.value.product_id, 
      {
        warehouses: allocations.value.map(item => ({
          warehouse_id: item.id,
          catalogs: item.items.map(({ inventory, ...catalog}) => ({
            ...catalog,
          })),
        })),
      },
    )
    await fetchAllocations()
    $notification.success({
      message: i18n.t('productAvailability.allocation_saved'),
    })
  } catch (error) {
    console.error(error)
  } finally {
    saving.value = false
  }
}

// ─── History Tab ────────────────────────────────────────────────────────────

/** Active sub tab within a warehouse tab: 'allocation' | 'history' */
const activeSubTab = ref('allocation')

/** Currently selected catalog_id for history view */
const historyCatalogId = ref(null)

/** Selected date range for history view - default to last 1 year */
const historyDates = ref([
  moment().subtract(1, 'year').add(1, 'day').startOf('day'),
  moment().endOf('day'),
])

const historyPage = ref(0)
const historyPageSize = ref(10)
const historyTotal = ref(0)
const historyRows = ref([])
const historyLoading = ref(false)
const historyError = ref(false)

/** Computes date range presets/shortcuts for history view */
const historyDateRangePresets = computed(() => {
  return {
    [i18n.t('productAvailability.ranges.today')]: [
      moment().startOf('day'),
      moment().endOf('day'),
    ],
    [i18n.t('productAvailability.ranges.this_month')]: [
      moment().startOf('month'),
      moment().endOf('month'),
    ],
    [i18n.t('productAvailability.ranges.this_year')]: [
      moment().startOf('year'),
      moment().endOf('year'),
    ],
    [i18n.t('productAvailability.ranges.last_7_days')]: [
      moment().subtract(6, 'days').startOf('day'),
      moment().endOf('day'),
    ],
    [i18n.t('productAvailability.ranges.last_30_days')]: [
      moment().subtract(29, 'days').startOf('day'),
      moment().endOf('day'),
    ],
    [i18n.t('productAvailability.ranges.last_year')]: [
      moment().subtract(1, 'year').add(1, 'day').startOf('day'),
      moment().endOf('day'),
    ],
  }
})

function getChannelInfo(channelId) {
  if (!channelId || !Array.isArray(businessChannels.value)) return null
  return businessChannels.value.find(c => c.id === Number(channelId))
}

/** Reset history state when active warehouse changes */
watch(selectedWarehouse, () => {
  historyCatalogId.value = null
  historyDates.value = [
    moment().subtract(1, 'year').add(1, 'day').startOf('day'),
    moment().endOf('day'),
  ]
  historyPage.value = 0
  historyRows.value = []
  historyTotal.value = 0
  historyError.value = false
})

function formatHistoryDate(iso) {
  if (!iso) return '-'
  return moment(iso).local().format('DD MMM YYYY, HH:mm:ss.SSS')
}

async function fetchHistory() {
  if (!historyCatalogId.value || !selectedWarehouse.value) return
  historyLoading.value = true
  historyError.value = false
  try {
    const startDate = historyDates.value?.[0]
      ? historyDates.value[0].clone().startOf('day').toISOString()
      : undefined
    const endDate = historyDates.value?.[1]
      ? historyDates.value[1].clone().endOf('day').toISOString()
      : undefined

    const { data } = await getStockAllocationHistory({
      catalog_id: historyCatalogId.value,
      warehouse_id: selectedWarehouse.value,
      business_id: product.value?.business_id,
      start_date: startDate,
      end_date: endDate,
      page: historyPage.value,
      size: historyPageSize.value,
    })
    historyRows.value = data?.data?.content || []
    historyTotal.value = data?.data?.totalElements ?? data?.data?.total_elements ?? 0
  } catch (err) {
    console.error(err)
    historyError.value = true
    historyRows.value = []
    historyTotal.value = 0
  } finally {
    historyLoading.value = false
  }
}

function onHistoryDatesChange() {
  historyPage.value = 0
  fetchHistory()
}

function onHistoryCatalogChange(catalogId) {
  historyCatalogId.value = catalogId
  historyPage.value = 0
  fetchHistory()
}

function onHistoryPageChange(page) {
  // a-pagination uses 1-based; API uses 0-based
  historyPage.value = page - 1
  fetchHistory()
}

function onHistorySizeChange(current, size) {
  historyPageSize.value = size
  historyPage.value = 0
  fetchHistory()
}

/** Flat catalog list derived from the currently active warehouse tab */
const historyCatalogOptions = computed(() => {
  if (!selectedWarehouse.value || !Array.isArray(allocations.value)) return []
  const wh = allocations.value.find(w => w.id === selectedWarehouse.value)
  return wh?.items || []
})

/** Auto-select catalog when switching to history tab or warehouse changes */
watch([activeSubTab, historyCatalogOptions], ([subTab, options]) => {
  if (subTab === 'history') {
    if (Array.isArray(options) && options.length > 0) {
      // If not selected or current selection is not in the list of options, select the first one
      const currentStillValid = options.some(opt => opt.catalog_id === historyCatalogId.value)
      if (!historyCatalogId.value || !currentStillValid) {
        onHistoryCatalogChange(options[0].catalog_id)
      } else {
        // If already selected and valid, refresh history to capture any updates
        fetchHistory()
      }
    }
  }
}, { immediate: true })

/** Filter SKU selector by typing — matches SKU or title */
function historyFilterOption(input, option) {
  const text = (option.componentOptions?.children?.[0]?.text || '').toLowerCase()
  return text.includes(input.toLowerCase())
}
</script>

<template>
  <div ref="wrapper">
    <div class="mb-5 d-flex page-title">
      <a href="#" style="color: #495057" class="py-1 mr-3" @click="$router.back()">
        <a-icon type="arrow-left" />
      </a>

      <div>
        <h1 class="h4">
          {{ $t('product.stock_allocation') }}
        </h1>
        <div class="product-title">
          {{ product?.detail?.title || '-' }}
        </div>
        <div>
          {{ $tc('product.variant', product?.catalogs?.length) }}
        </div>
      </div>
    </div>

    <div style="position: relative;">
      <div v-if="isLoading" class="spinner-wrapper">
        <a-spin spinning size="large" />
      </div>

      <!-- Warehouse tabs (sidebar kiri) -->
      <a-tabs
        v-else
        :tab-position="tabPosition"
        color="primary"
        size="small"
        class="allocation-tabs"
        :animated="false"
        :active-key="selectedWarehouse"
        @change="
          $router.replace({ query: { ...$route.query, warehouse_id: $event } })
        "
      >
        <template v-if="tabPosition === 'left'" #tabBarExtraContent>
          <div class="w-full text-center mb-3" style="margin-right: -1px; letter-spacing: 1px; text-transform: uppercase; color: #495057; border-bottom: 1px solid #e4e9f0; border-right: 1px solid #F8F9FA">
            {{ $t('productAvailability.warehouse') }}
          </div>
        </template>

        <a-tab-pane
          v-for="warehouse in allocations"
          :key="warehouse.id"
          :warehouse-id="warehouse.id"
        >
          <template #tab>
            <span :warehouse-id="warehouse.id">
              {{ warehouse.name }}
            </span>
            <a-badge
              :show-zero="false"
              :count="warehouse.counts"
              :offset="[15, 0]"
              style="position: absolute; right: 1.25rem"
              :number-style="{
                backgroundColor: 'rgb(255, 10, 84, .5)',
                fontSize: '80%',
              }"
            />
          </template>

          <!-- Sub-tabs: Alokasi Stok | Riwayat -->
          <a-tabs
            v-model="activeSubTab"
            class="warehouse-inner-tabs"
            :animated="false"
            size="small"
          >
            <!-- ── Sub-tab 1: Alokasi Stok ── -->
            <a-tab-pane key="allocation" :tab="$t('productAvailability.allocation_tab')">
              <Transition name="slide-fade">
                <div v-if="warehouse.id === selectedWarehouse">
                  <a-card
                    v-for="item in warehouse.items"
                    :id="item.catalog_id"
                    :key="item.catalog_id"
                    :data-catalog-id="item.catalog_id"
                    class="mb-5"
                  >
                    <template #title>
                      <div class="catalog-info">
                        <div>
                          <div class="catalog-info__sku">
                            {{ item.sku }}
                          </div>
                          <div class="catalog-info__title">
                            {{ item.title }}
                          </div>

                          <div class="stock-detail">
                            <a-tooltip :title="$t('productAvailability.on_hand_stock')">
                              <IconStockOnHand width="12" />
                              {{ item.inventory.on_hand || 0 }}
                            </a-tooltip>
                            <a-tooltip :title="$t('productAvailability.allocated_stock')">
                              <IconStockAllocated width="12" />
                              {{ item.inventory.allocated || 0 }}
                            </a-tooltip>
                            <a-tooltip :title="$t('productAvailability.sellable_stock_short')">
                              <IconStockSellable width="12" />
                              {{ item.inventory.sellable || 0 }}
                            </a-tooltip>
                          </div>
                        </div>

                        <div class="ml-auto">
                          <div class="mb-2">
                            {{ $t('productAvailability.enable_stock_push') }}
                          </div>
                          <a-switch v-model="item.enable" />
                        </div>
                      </div>
                    </template>

                    <table class="channel-allocation">
                      <colgroup>
                        <col style="min-width: 100px" />
                        <col style="width: 100px" />
                        <col style="width: 100px" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>{{ $t('productAvailability.channel') }}</th>
                          <th>{{ $t('productAvailability.allocation') }}</th>
                          <th class="text-right">
                            {{ $t('productAvailability.stock') }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-if="!item.allocations.length">
                          <tr>
                            <td colspan="3">
                              <a-empty />
                            </td>
                          </tr>
                        </template>
                        <template v-else>
                          <tr
                            v-for="(channel, index) in item.allocations"
                            :key="[channel.channel_id, index].join('--')"
                            class="channel-allocation--item"
                          >
                            <td>
                              <div class="channel-info">
                                <div>
                                  <img :src="salesChannelIcon(channel.info?.code)" />
                                </div>
                                <div v-if="channel.info">
                                  <div class="small text-secondary channel-info__id">
                                    #{{ channel.info?.id }}
                                  </div>
                                  {{ channel.info?.title || '-' }}
                                </div>
                                <div
                                  v-else
                                  class="text-muted small"
                                  style="font-style: italic"
                                >
                                  {{ $t('productAvailability.deleted_channel') }}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div class="d-flex align-items-center">
                                <div class="mb-0 mr-3">
                                  <a-tooltip :title="!item.enable ? $t('productAvailability.stock_push_disabled_sku') : `${$t('productAvailability.stock_push_label')}: ${channel.enable_sync ? $t('productAvailability.stock_push_enabled') : $t('productAvailability.stock_push_disabled')}`">
                                    <a-switch
                                      v-model="channel.enable"
                                      size="small"
                                      :disabled="!item.enable"
                                    />
                                  </a-tooltip>
                                </div>
                                <div>
                                  <a-input-number
                                    v-model="channel.allocate_value"
                                    :min="0"
                                    :max="100"
                                    :formatter="value => `${value}%`"
                                    :parser="value => value.replace('%', '')"
                                  />
                                </div>
                              </div>
                            </td>
                            <td class="text-right">
                              {{
                                Intl.NumberFormat('id-ID').format(
                                  calculateAllocationAmount(channel, item.inventory),
                                )
                              }}
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </a-card>
                </div>
              </Transition>
            </a-tab-pane>

            <!-- ── Sub-tab 2: Riwayat (per gudang ini) ── -->
            <a-tab-pane
              v-if="warehouse.id === selectedWarehouse"
              key="history"
              :tab="$t('productAvailability.allocation_history_tab')"
              class="history-tabpane"
            >
              <div class="history-panel">
                <!-- Catalog/Variant & Date range selector -->
                <div class="history-filters">
                  <div class="history-filter-item sku-filter">
                    <div class="history-filter-label">
                      SKU:
                    </div>
                    <a-select
                      id="history-catalog-select"
                      class="history-catalog-select"
                      :placeholder="$t('productAvailability.allocation_history_select_catalog')"
                      :value="historyCatalogId"
                      show-search
                      :filter-option="historyFilterOption"
                      allow-clear
                      option-label-prop="label"
                      @change="onHistoryCatalogChange"
                    >
                      <a-select-option
                        v-for="cat in historyCatalogOptions"
                        :key="cat.catalog_id"
                        :value="cat.catalog_id"
                        :label="cat.sku"
                        :title="cat.sku"
                      >
                        {{ cat.sku }} — {{ cat.title }}
                      </a-select-option>
                    </a-select>
                  </div>

                  <div class="history-filter-item date-filter">
                    <div class="history-filter-label">
                      {{ $t('productAvailability.allocation_history_date') }}:
                    </div>
                    <a-range-picker
                      v-model="historyDates"
                      class="history-range-picker"
                      format="DD MMM YYYY"
                      :placeholder="[
                        $t('productAvailability.start_date'),
                        $t('productAvailability.end_date'),
                      ]"
                      :ranges="historyDateRangePresets"
                      allow-clear
                      @change="onHistoryDatesChange"
                    />
                  </div>
                </div>

                <!-- States -->
                <div v-if="!historyCatalogId" class="history-placeholder">
                  <a-empty :description="$t('productAvailability.allocation_history_select_catalog')" />
                </div>

                <div v-else-if="historyLoading" class="spinner-wrapper">
                  <a-spin spinning size="large" />
                </div>

                <div v-else-if="historyError" class="history-placeholder">
                  <a-empty :description="$t('productAvailability.allocation_history_error')" />
                </div>

                <template v-else>
                  <div v-if="!historyRows.length" class="history-placeholder">
                    <a-empty :description="$t('productAvailability.allocation_history_empty')" />
                  </div>

                  <template v-else>
                    <div class="history-table-wrapper">
                      <table class="history-table">
                        <thead>
                          <tr>
                            <th>{{ $t('productAvailability.allocation_history_date') }}</th>
                            <th>{{ $t('productAvailability.allocation_history_scope') }}</th>
                            <th>{{ $t('productAvailability.allocation_history_channel') }}</th>
                            <th>{{ $t('productAvailability.allocation_history_prev_status') }}</th>
                            <th>{{ $t('productAvailability.allocation_history_new_status') }}</th>
                            <th>{{ $t('productAvailability.allocation_history_user') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in historyRows" :key="row.id">
                            <td>{{ formatHistoryDate(row.created_at) }}</td>
                            <td>
                              <a-tag :color="row.scope === 'CHANNEL' ? 'blue' : 'purple'">
                                {{ row.scope === 'CHANNEL'
                                  ? $t('productAvailability.allocation_history_channel_scope')
                                  : $t('productAvailability.allocation_history_warehouse_scope') }}
                              </a-tag>
                            </td>
                            <td>
                              <div v-if="row.scope === 'CHANNEL'" class="channel-info">
                                <template v-if="getChannelInfo(row.channel_id)">
                                  <div>
                                    <img :src="salesChannelIcon(getChannelInfo(row.channel_id)?.code)" />
                                  </div>
                                  <div>
                                    <div class="small text-secondary channel-info__id">
                                      #{{ row.channel_id }}
                                    </div>
                                    {{ getChannelInfo(row.channel_id)?.title || '-' }}
                                  </div>
                                </template>
                                <template v-else>
                                  <div v-if="row.channel_code">
                                    <img :src="salesChannelIcon(row.channel_code)" />
                                  </div>
                                  <div>
                                    <div v-if="row.channel_id" class="small text-secondary channel-info__id">
                                      #{{ row.channel_id }}
                                    </div>
                                    {{ row.channel_code || '-' }}
                                  </div>
                                </template>
                              </div>
                              <div v-else class="text-muted">
                                -
                              </div>
                            </td>
                            <td>
                              <span v-if="row.previous_status === null" class="text-muted">-</span>
                              <span
                                v-else
                                :class="['status-pill', row.previous_status ? 'status-pill--active' : 'status-pill--inactive']"
                              >
                                {{ row.previous_status
                                  ? $t('productAvailability.allocation_history_enabled')
                                  : $t('productAvailability.allocation_history_disabled') }}
                              </span>
                            </td>
                            <td>
                              <span
                                :class="['status-pill', row.new_status ? 'status-pill--active' : 'status-pill--inactive']"
                              >
                                {{ row.new_status
                                  ? $t('productAvailability.allocation_history_enabled')
                                  : $t('productAvailability.allocation_history_disabled') }}
                              </span>
                            </td>
                            <td>{{ row.user_name || '-' }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div class="history-pagination">
                      <a-pagination
                        :current="historyPage + 1"
                        :total="historyTotal"
                        :page-size="historyPageSize"
                        :page-size-options="['10', '20', '50', '100']"
                        show-size-changer
                        show-less-items
                        :show-total="(total, range) => $t('productAvailability.pagination_total', { start: range[0], end: range[1], total })"
                        @change="onHistoryPageChange"
                        @showSizeChange="onHistorySizeChange"
                      />
                    </div>
                  </template>
                </template>
              </div>
            </a-tab-pane>
          </a-tabs>
        </a-tab-pane>
      </a-tabs>
    </div>

    <!-- Tombol Simpan sticky di bawah (hanya relevan untuk alokasi) -->
    <div v-if="activeSubTab === 'allocation'" class="allocation-footer">
      <a-button
        type="primary"
        size="large"
        :loading="saving"
        :disabled="saving"
        @click="saveAllocation"
      >
        {{ $t('common.save') }}
      </a-button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.spinner-wrapper {
  background-color: rgba(255, 255, 255, 0.8);
  height: 300px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.page-title {
  color: rgb(73, 80, 87);
  letter-spacing: 2px;
  text-transform: uppercase;
}

.product-title {
  font-size: 16px;
  font-weight: 500;
  // margin-top: -.5rem;
  letter-spacing: 0px;
  text-transform: none;
}

.allocation-footer {
  position: sticky;
  bottom: 0;
  background-color: #f8f9fa;
  padding: 1rem;
  display: flex;
  justify-content: flex-end;
  z-index: 10;
}

.allocation-tabs.ant-tabs::v-deep {

  .ant-tabs-left-bar {
    
    display: flex;
    flex-direction: column;

    .ant-tabs-extra-content {
      order: -1;
    }

    .ant-tabs-tab {
      text-align: left;
      border-radius: 4px;
      margin-bottom: 2px;
      padding: 0.5rem 2rem 0.5rem 1rem;
      min-width: 175px;
      max-width: 200px;
      text-wrap: auto;
      margin-right: 0.5rem !important;
      display: flex;
      font-size: 13px;

      > *:not(.ant-badge) {
        flex-grow: 1;
      }

      .ant-badge {
        font-size: 12px;
        line-height: 1.5;
      }

      &.ant-tabs-tab-active {
        background-color: #ffebf1;
      }
    }
  }

  .ant-tabs-top-bar {
    .ant-tabs-tab {
      padding: 8px 20px;

      .ant-badge {
        font-size: 11px;
        top: 0;
        // position: relative !important;
        right: -.75rem !important;
        // transform: none;

        sup {
          right: 0 !important;
        }
      }
    }
  }
}

.catalog-info {
  display: flex;
  gap: 0 12px;
  align-items: center;
  white-space: initial;

  > *:nth-child(1) {
    flex-shrink: 1;
  }

  > *:nth-child(2) {
    text-align: right;
    font-size: 12px;
    font-weight: normal;
    flex-shrink: 0;
  }

  &__sku {
    font-size: 12px;
    color: #444444;
    letter-spacing: 0.025rem;
    font-family: monospace;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    color: var(--kit-color-primary);
  }
}

.stock-detail {
  display: flex;
  gap: 0 12px;
  font-size: 14px;
  color: #868686;
  letter-spacing: 0.025rem;
  font-family: monospace;

  > * {
    display: flex;
    gap: 0 4px;
    align-items: center;
  }
}

.channel-info {
  display: flex;
  gap: 0 12px;
  align-items: center;

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
    display: block;
  }

  &__id {
    font-size: 11px;
    color: #656565;
    letter-spacing: 0.025rem;
    font-family: monospace;
    margin-bottom: 0;
    line-height: 1.25;
  }
}

.channel-allocation {
  width: 100%;

  thead {
    font-weight: 500;
    font-size: 90%;
    border-bottom: 1px solid #efefef;
  }

  th {
    padding: 0.5rem 0.5rem;
  }

  tbody {
    td {
      padding: 1rem 0.5rem;
    }

    tr:not(:last-child) {
      border-bottom: 1px solid #efefef;
    }
  }
}

.warehouse-inner-tabs.ant-tabs::v-deep {
  > .ant-tabs-bar {
    margin-bottom: 1rem;
    border-bottom: 1px solid #e8e8e8;
  }

  .ant-tabs-tab {
    font-size: 13px;

    &.ant-tabs-tab-active {
      color: #ff0a54;
      font-weight: 500;
    }
  }

  .ant-tabs-ink-bar {
    background-color: #ff0a54;
  }
}

.history-panel {
  /* padding: 0.5rem 0; */
}

.spinner-wrapper,
.history-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem 0;
}

.history-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 1.5rem;
}

.history-filter-item {
  display: flex;
  align-items: center;

  &.sku-filter {
    flex: 1;
    min-width: 250px;
  }

  &.date-filter {
    flex-shrink: 0;
  }
}

.history-filter-label {
  font-weight: 500;
  color: #495057;
  font-size: 13.5px;
  margin-right: 12px;
  display: flex;
  align-items: center;
}

.history-catalog-select {
  width: 100%;

  &::v-deep .ant-select-selection-selected-value {
    padding-right: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.history-range-picker.ant-calendar-picker::v-deep {
  width: 320px;

  .ant-calendar-picker-input {
    height: 38px;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    border-color: #d9d9d9;
    transition: all 0.2s ease-in-out;
    display: flex;
    align-items: center;
    padding: 0 12px;

    &:hover {
      border-color: #ff0a54;
    }
  }

  .ant-calendar-range-picker-input {
    font-size: 13.5px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.65);
    height: 100%;
    width: 44%;
    text-align: center;
    border: none;
    outline: none;
    background: transparent;

    &::placeholder {
      color: #bfbfbf;
      font-weight: 400;
    }
  }

  .ant-calendar-range-picker-separator {
    font-size: 13.5px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.45);
    width: 12%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    transform: translateY(1.5px);
  }

  &.ant-calendar-picker-focused .ant-calendar-picker-input {
    border-color: #ff0a54;
    box-shadow: 0 0 0 2px rgba(255, 10, 84, 0.1);
  }
}

.history-catalog-select.ant-select::v-deep {
  .ant-select-selection {
    border-radius: 6px;
    height: 38px;
    display: flex;
    align-items: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    border-color: #d9d9d9;
    transition: all 0.2s ease-in-out;

    &:hover {
      border-color: #ff0a54;
    }
  }

  .ant-select-selection__rendered {
    line-height: 36px;
    margin-left: 12px;
    font-size: 13.5px;
    font-weight: 500;
  }

  &.ant-select-focused .ant-select-selection {
    border-color: #ff0a54;
    box-shadow: 0 0 0 2px rgba(255, 10, 84, 0.1);
  }
}

.history-placeholder {
  padding: 3rem 0;
  display: flex;
  justify-content: center;
}

.history-table-wrapper {
  height: calc(100vh - 450px);
  min-height: 380px;
  overflow-y: auto;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  background: #ffffff;

  // Premium custom scrollbar
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #e4e9f0;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #ccd4e0;
  }
}

.history-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  background: #ffffff;

  thead {
    font-weight: 600;
    background-color: #fafafa;
    color: rgba(0, 0, 0, 0.85);

    th {
      position: sticky;
      top: 0;
      z-index: 2;
      background-color: #fafafa;
      padding: 8px 16px;
      text-align: center;
      white-space: nowrap;
      border-bottom: 1px solid #e8e8e8;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #495057;

      &:nth-child(3) {
        text-align: left;
      }
    }
  }

  tbody {
    tr {
      transition: background-color 0.15s ease;
    }

    td {
      padding: 6px 16px;
      height: 52px;
      vertical-align: middle;
      color: rgba(0, 0, 0, 0.65);
      border-bottom: 1px solid #e8e8e8;
      text-align: center;

      &:nth-child(3) {
        text-align: left;
      }
    }

    tr:last-child td {
      border-bottom: none;
    }

    tr:hover {
      background-color: #fafafa;
    }
  }

  .ant-tag {
    font-size: 12px;
    font-weight: 500;
    padding: 3px 10px;
    border-radius: 4px;
    line-height: 1.4;
    margin-right: 0;

    &.ant-tag-purple {
      background: #f3f0ff;
      color: #7048e8;
      border-color: #e5dbff;
    }

    &.ant-tag-blue {
      background: #e7f5ff;
      color: #1c7ed6;
      border-color: #d0ebff;
    }
  }
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;

  &--active {
    background-color: #e6ffed;
    color: #28a745;
    border: 1px solid #c3e6cb;
  }

  &--inactive {
    background-color: #ffeef0;
    color: #cb2431;
    border: 1px solid #f5c6cb;
  }
}

.history-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  flex-shrink: 0;
}

</style>
